import { Fragment, useMemo } from 'react';
import { Cell } from '@tanstack/react-table';

import TRow from './TRow/TRow';
import SummaryRow from '../SummaryRow';

import { TBodyProps } from './TBody.props';
import { useReactTableControllerInstance } from '../hooks/useReactTableController';

import styles from './TBody.module.css';

export default function TBody<T>({
  tableId,
  table,
  columnOrder,
  getRowProps,
  getCellProps,
  renderSubComponent,
  summaryRow,
  data = [],
  onRowClick,
  onRowDoubleClick,
  onCellClick,
  rowHeight = 1,
  emptyPlaceholder,
}: TBodyProps<T> & { tableId: string }) {
  const controller = useReactTableControllerInstance(tableId);

  const onCellClickHandler = ({ row, column }: Cell<T, unknown>) => {
    controller.setActiveRow(row.id);
    controller.setActiveCell(column.id);
  };

  // Calculate summary data if summaryRow is provided
  const summaryData = useMemo(() => {
    if (!summaryRow) return null;

    if (typeof summaryRow === 'function') {
      return summaryRow(data);
    }

    return summaryRow;
  }, [summaryRow, data]);

  const rows = table.getRowModel().rows;

  if (rows.length === 0 && emptyPlaceholder) {
    return (
      <div className={styles.emptyRow}>
        <span className={styles.emptyRowText}>{emptyPlaceholder}</span>
      </div>
    );
  }

  return (
    <div>
      {rows.map(row => (
        <Fragment key={row.id}>
          <TRow<T>
            tableId={tableId}
            row={row}
            columnOrder={columnOrder}
            getRowProps={getRowProps}
            getCellProps={getCellProps}
            onCellClickHandler={onCellClickHandler}
            onRowClick={onRowClick}
            onRowDoubleClick={onRowDoubleClick}
            onCellClick={onCellClick}
            rowHeight={rowHeight}
          />

          {row.getIsExpanded() && renderSubComponent && (
            <div className={styles.expanded}>{renderSubComponent({ row })}</div>
          )}
        </Fragment>
      ))}

      {/* Render summary row if provided */}
      {summaryData && (
        <SummaryRow<T>
          summaryData={summaryData}
          columns={table.getAllColumns().map(col => col.columnDef)}
          columnOrder={columnOrder}
        />
      )}
    </div>
  );
}
